<template>
  <HighlightBox
    v-if="isSuccess"
    class="rounded-10 text-center mx-auto bg-white"
    type="recommendation"
  >
    <p class="subtitle2">{{ $t('email_alert.success_title') }}</p>
    <p class="my-8">{{ $t('email_alert.success_message') }}</p>
  </HighlightBox>
  <div v-else class="mx-auto max-w-lg text-center">
    <div :class="innerClass">
      <SliceTitle v-if="title" class="subtitle1 mb-6 md:mb-8" :value="title" />
      <template v-if="description">
        <p v-if="typeof description === 'string'" class="md:text-18 font-medium mb-6">
          {{ description }}
        </p>
        <PortableText v-else class="md:text-18 font-medium mb-6" :content="description" />
      </template>

      <MSpinner v-if="isLoading" class="my-8 mx-auto" />
      <template v-else>
        <div
          v-if="hasError"
          class="p-4 mb-4 rounded-10 text-center bg-red-50 text-red-900 font-semibold"
        >
          {{ error }}
        </div>

        <form v-if="isInputVisible" @submit.prevent="subscribe">
          <MSelect
            v-if="options.length"
            class="bg-white mb-4"
            selection-class="py-3 pr-3"
            :items="options"
            item-text="label"
            :outlined="true"
            :placeholder="$t('email_alert.select_placeholder')"
            :header-text="$t('email_alert.alert_frequency')"
            v-model="selection"
          >
            <template #item="{ item }">
              <span>{{ item.label }}</span>
              <span v-if="item.description" class="ml-3 text-gray-400 font-medium text-16">{{
                item.description
              }}</span>
            </template>
          </MSelect>

          <input
            class="flex w-full border-3 bg-white rounded-4 border-gray-200 text-18 font-semibold p-3 outline-none h-16"
            type="email"
            pattern='^([^<>()\[\]\\.,;:\s@"]+(?:\.[^<>()\[\]\\.,;:\s@"]+)*)@((?:[a-zA-Z\-0-9]+\.)+[a-zA-Z][a-zA-Z0-9-]*[a-zA-Z0-9])$'
            @invalid="
              ;($event.target as HTMLInputElement).setCustomValidity($t('errors.invalid_email'))
            "
            @input=";($event.target as HTMLInputElement).setCustomValidity('')"
            v-model="email"
            :aria-label="$t('email_address')"
            :placeholder="$t('email_address')"
            required
          />
          <button
            v-if="isInputVisible"
            type="submit"
            class="btn btn-amber text-17 items-center flex justify-center h-16 align-bottom mt-4 w-full"
          >
            {{ ctaLabel || $t('submit') }}
          </button>
          <p class="caption mt-4">
            {{ $t('email_alert.by_continuing') }}
            {{ $t('unsubscribe_anytime') }}
            <a :href="$t('privacy_policy_url')" class="underline" target="_blank" rel="noopener">{{
              $t('read_policy')
            }}</a>
          </p>
        </form>

        <button
          v-if="!isInputVisible"
          @click="onInputVisibilityToggle"
          class="btn btn-amber text-17 items-center flex justify-center h-16 align-bottom mt-4 w-full"
        >
          {{ $t('email_alert.create_alert') }}
        </button>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { TitleStyle } from '@core/types/generated'
import type { GQLBlock } from '@core/types/generated/text'
import type { EmailAlertFrequency, FormStatus, EmailAlert } from './type'

const props = withDefaults(
  defineProps<{
    alert: EmailAlert
    options: EmailAlertFrequency[]
    title?: { text: string; style: TitleStyle } | null
    description?: string | GQLBlock[] | null
    ctaLabel?: string | null
    isInputVisible?: boolean
    innerClass?: object
  }>(),
  { isInputVisible: true },
)

const emit = defineEmits(['toggle-input-visibility', 'subscribe'])

const { $t } = useNuxtApp()
const logger = useLogger('email-alert')
const { subscriptions } = useRuntimeConfig().public

const selection: Ref<string | null> = ref(props.options.length ? props.options[0].value : null)
const email: Ref<string | null> = ref(null)

const error: Ref<string | null> = ref(null)
const status: Ref<FormStatus> = ref('INITIAL')

const isLoading = computed(() => status.value === 'LOADING')
const isSuccess = computed(() => status.value === 'SUCCESS')
const hasError = computed(() => status.value === 'ERROR')

function onInputVisibilityToggle(event: Event) {
  emit('toggle-input-visibility', event, !props.isInputVisible)
}

function onError(err: Error) {
  setSignedIn(true)
  status.value = 'ERROR'
  error.value = $t('errors.something_went_wrong')
  logger.error(err)
}

async function subscribe() {
  if (!subscriptions.apiEndpoint) {
    return onError(Error('The subscriptions API endpoint is missing'))
  }

  try {
    error.value = null
    status.value = 'LOADING'

    const body = {
      ...props.alert.requestBody,
      email: email.value,
      subscriptionCase: selection.value,
    }

    const resp = await $fetch<Response, string>(
      `${subscriptions.apiEndpoint}${props.alert.requestPath}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )

    if (resp.status >= 400) {
      onError(Error(resp.statusText))
      return
    }
    setSignedIn()
    status.value = 'SUCCESS'
  } catch (e) {
    onError(e instanceof Error ? e : Error(JSON.stringify(e)))
  }

  emit('subscribe')
}
</script>
